import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {Route, Routes} from 'react-router-dom';

import {InitLayout} from '../components/Layout/InitLayout';
import {SidebarLayout} from '../components/Layout/SidebarLayout';
import {LoaderSpinner} from '../components/LoaderSpinner';
import {NotFound} from '../components/NotFound';
import {getUserRoles, hasRoleAccess} from '../components/ProtectedComponent';
import {Restricted} from '../components/Restricted';
import {FollowupQuestions} from '../pages/formResponses/FollowupQuestions';
import {Home} from '../pages/home';

const Organizations = lazy(() => import('../pages/organizations'));
const Roles = lazy(() => import('../pages/roles'));
const Plans = lazy(() => import('../pages/plans'));
const Users = lazy(() => import('../pages/users'));
const Locations = lazy(() => import('../pages/locations'));
const LocationsTypes = lazy(() => import('../pages/locationsTypes'));
const Projects = lazy(() => import('../pages/projects'));
const Bills = lazy(() => import('../pages/bills'));
const BillingDashboard = lazy(() => import('../pages/billingDashboard'));
const NewForm = lazy(() => import('../pages/forms/form/NewForm'));
const Forms = lazy(() => import('../pages/forms'));
const Documents = lazy(() => import('../pages/documents'));
const EventType = lazy(() => import('../pages/events'));
const Analytics = lazy(() => import('../pages/analytics'));
const Report = lazy(() => import('../pages/analytics/Report'));
const ObjectivesTypes = lazy(() => import('../pages/objectivesTypes'));
const Tours = lazy(() => import('../pages/tours'));
const FieldRoutes = lazy(() => import('../pages/fieldRoutes'));
const TeamLocation = lazy(() => import('../pages/teamLocation'));
const Schedule = lazy(() => import('../pages/schedule'));
const Incidents = lazy(() => import('../pages/incidents'));
const Bulletin = lazy(() => import('../pages/bulletin'));
const CustomFields = lazy(() => import('../pages/customFields'));
const Kpis = lazy(() => import('../pages/kpis'));
const AssignKpis = lazy(() => import('../pages/assignKpis'));
const Periods = lazy(() => import('../pages/periods'));
const Policies = lazy(() => import('../pages/policies'));
const FormResponses = lazy(() => import('../pages/formResponses'));
const BillsTypes = lazy(() => import('../pages/billsTypes'));
const SubPolicies = lazy(() => import('../pages/policies/SubPolicies'));
const ContentReportChecks = lazy(
  () => import('../pages/reports/ContentReportChecks'),
);
const ContentReportMileage = lazy(
  () => import('../pages/reports/ContentReportMileage'),
);
const ProductCategories = lazy(() => import('../pages/productCategories'));
const Products = lazy(() => import('../pages/products'));
const Orders = lazy(() => import('../pages/orders'));
const Dashboard = lazy(() => import('../pages/dashboard'));

interface IRouteMapping {
  allowedRoles: string[];
  /** Oculta la ruta a quien tenga alguno, aunque esté en `allowedRoles`. */
  deniedRoles?: string[];
  path: string;
  element: React.JSX.Element;
  children?: IRouteMapping[];
}

const routeMapping: IRouteMapping[] = [
  {
    path: '/home',
    allowedRoles: ['super-admin', 'supervisor', 'project-admin', 'org-admin'],
    element: <Home />,
  },
  {
    path: '/dashboard',
    allowedRoles: ['happify-graph.dashboard.view'],
    element: <Dashboard />,
  },
  {
    path: '/organization',
    allowedRoles: ['super-admin'],
    element: <Organizations />,
  },
  {
    path: '/projects',
    allowedRoles: ['super-admin'],
    element: <Projects />,
  },
  {
    path: '/plans',
    allowedRoles: ['super-admin'],
    element: <Plans />,
  },
  {
    path: '/roles',
    allowedRoles: ['super-admin'],
    element: <Roles />,
  },
  {
    path: '/users',
    allowedRoles: [
      'super-admin',
      'project-admin',
      'happify-directory-service.users.admin',
    ],
    element: <Users />,
  },
  {
    path: '/users/:id',
    allowedRoles: [
      'super-admin',
      'project-admin',
      'happify-directory-service.users.admin',
    ],
    element: <Users />,
  },
  {
    path: '/event-type',
    allowedRoles: ['super-admin'],
    element: <EventType />,
  },
  {
    path: '/schedule',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Schedule />,
  },
  {
    path: '/locationsTypes',
    allowedRoles: ['super-admin'],
    element: <LocationsTypes />,
  },
  {
    path: '/locations',
    allowedRoles: ['super-admin', 'happify-location-service.location.admin'],
    element: <Locations />,
  },
  // `beta-tracking` sustituye el módulo de recorridos por su versión nueva en
  // vez de sumar una segunda entrada: quien lo tiene deja de ver el viejo.
  {
    path: '/tours',
    allowedRoles: ['super-admin', 'supervisor'],
    deniedRoles: ['happify-admin-website.beta-tracking'],
    element: <Tours />,
  },
  {
    path: '/field-routes',
    allowedRoles: ['super-admin', 'happify-admin-website.beta-tracking'],
    element: <FieldRoutes />,
  },
  {
    path: '/teamLocation',
    allowedRoles: ['super-admin', 'happify-location-service.team-map'],
    element: <TeamLocation />,
  },
  {
    path: '/policies',
    allowedRoles: ['super-admin'],
    element: <Policies />,
  },
  {
    path: '/policies/sub-policies',
    allowedRoles: ['super-admin'],
    element: <SubPolicies />,
  },
  {
    path: '/policies/sub-policies/:idOrganization/:id',
    allowedRoles: ['super-admin'],
    element: <SubPolicies />,
  },
  {
    path: '/billsTypes',
    allowedRoles: ['super-admin'],
    element: <BillsTypes />,
  },
  {
    path: '/bills',
    allowedRoles: ['super-admin', 'org-admin'],
    element: <Bills />,
  },
  {
    path: '/billing-dashboard',
    allowedRoles: ['super-admin'],
    element: <BillingDashboard />,
  },
  {
    path: '/incidents',
    allowedRoles: ['super-admin'],
    element: <Incidents />,
  },
  {
    path: '/bulletin',
    allowedRoles: ['super-admin', 'org-admin', 'supervisor'],
    element: <Bulletin />,
  },
  {
    path: '/bulletin/:id',
    allowedRoles: ['super-admin', 'org-admin', 'supervisor'],
    element: <Bulletin />,
  },
  {
    path: '/forms',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.list'],
    element: <Forms />,
  },
  {
    path: '/forms/edit/',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.create'],
    element: <NewForm />,
  },
  {
    path: '/forms/edit/:id',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.edit'],
    element: <NewForm />,
  },
  {
    path: '/answers',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.answer-reader'],
    element: <FormResponses />,
    children: [
      {
        path: ':idSet',
        allowedRoles: [
          'super-admin',
          'happify-forms-service.forms.answer-reader',
        ],
        element: <FollowupQuestions />,
      },
    ],
  },
  {
    path: '/objectives-types',
    allowedRoles: ['super-admin'],
    element: <ObjectivesTypes />,
  },
  {
    path: '/documents',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Documents />,
  },
  {
    path: '/analytics',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Analytics />,
  },
  {
    path: '/analytics/report',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Report />,
  },
  {
    path: '/analytics/report/:id',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Report />,
  },
  {
    path: '/report-checks',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <ContentReportChecks />,
  },
  {
    path: '/report-mileage',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <ContentReportMileage />,
  },
  {
    path: '/kpis',
    allowedRoles: ['super-admin', 'happify-kpi-service.kpi.web-access'],
    element: <Kpis />,
  },
  {
    path: '/periods',
    allowedRoles: ['super-admin', 'happify-kpi-service.kpi-period.web-access'],
    element: <Periods />,
  },
  {
    path: '/assign-kpis',
    allowedRoles: [
      'super-admin',
      'happify-kpi-service.kpi-assignment.web-access',
    ],
    element: <AssignKpis />,
  },
  {
    path: '/custom-fields',
    allowedRoles: ['super-admin'],
    element: <CustomFields />,
  },
  {
    path: '/product-categories',
    allowedRoles: ['hap-commerce-service.commerce.user'],
    element: <ProductCategories />,
  },
  {
    path: '/products',
    allowedRoles: ['hap-commerce-service.commerce.user'],
    element: <Products />,
  },
  {
    path: '/orders',
    allowedRoles: [
      'admin',
      'org-admin',
      'order_supervisor',
      'order_taker',
      'hap-commerce-service.commerce.user',
    ],
    element: <Orders />,
  },
];

export function AppRouter() {
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    setRoles(getUserRoles());
  }, []);

  // Función recursiva para renderizar rutas y sus hijos
  const renderRoutes = useCallback(
    (routesToRender: IRouteMapping[], parentPath = '') => {
      return routesToRender
        .filter(route =>
          hasRoleAccess(roles, route.allowedRoles, route.deniedRoles),
        )
        .map(route => {
          const fullPath = `${parentPath}${route.path}`;

          if (!route.children || route.children.length === 0) {
            // Renderiza una ruta simple
            return (
              <Route
                key={fullPath}
                path={fullPath}
                element={
                  <Suspense fallback={<LoaderSpinner />}>
                    {route.element}
                  </Suspense>
                }
              />
            );
          }

          return (
            <Route
              key={fullPath}
              path={fullPath}
              element={
                <Suspense fallback={<LoaderSpinner />}>
                  {route.element}
                </Suspense>
              }>
              {renderRoutes(route.children)}
            </Route>
          );
        });
    },
    [roles],
  );

  const routes = useMemo(() => {
    // Llama a la función recursiva con el mapeo de rutas principal
    return renderRoutes(routeMapping, '/:idOrganization');
  }, [renderRoutes]);

  if (roles.length === 0) {
    return <LoaderSpinner />;
  }

  return (
    <Routes>
      <Route element={<SidebarLayout />}>{routes}</Route>
      <Route path='/' element={<InitLayout />} />
      <Route path='*' element={<Restricted />} />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}
